import { useState } from 'react';

import { Mail, MessageSquare, Phone, PhoneIncoming, PhoneOutgoing, UserCheck } from 'lucide-react';

import { OperatorCallDialog } from '@/components/contacts/OperatorCallDialog';
import { TagBadge } from '@/components/contacts/TagBadge';
import { ToolWatchBell } from '@/components/contacts/ToolWatchBell';
import { useAcknowledgeToolWatch } from '@/hooks/useContacts';
import { useTagDefinitions } from '@/hooks/useTagDefinitions';
import { clearedByLabel } from '@/lib/message-time';
import { cn } from '@/lib/utils';
import { useToolWatchStore } from '@/stores/tool-watch.store';

import { ActiveIndicator } from './ActiveIndicator';
import { ContactContextMenu } from './ContactContextMenu';

import type { ContactWithCounts, LastInteractionPreview } from '@repo/shared-types';

const MESSAGE_CHANNEL_ICONS: Record<string, typeof Phone> = {
  PSTN: Phone,
  WEBRTC: Phone,
  WHATSAPP: MessageSquare,
  SMS: MessageSquare,
  WEB_WIDGET: MessageSquare,
  EMAIL: Mail,
};

/** WhatsApp-style relative stamp: time today, weekday this week, date otherwise. */
export const formatRelative = (iso: string): string => {
  const date = new Date(iso);
  const now = new Date();
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dayDiff = Math.round((startOfDay(now).getTime() - startOfDay(date).getTime()) / 86_400_000);
  if (dayDiff <= 0) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (dayDiff === 1) return 'Yesterday';
  if (dayDiff < 7) return date.toLocaleDateString([], { weekday: 'long' });
  return date.toLocaleDateString([], { day: 'numeric', month: 'short', year: '2-digit' });
};

const previewText = (preview: LastInteractionPreview | null | undefined): string => {
  if (!preview) return 'No interactions yet';
  if (preview.type === 'call') {
    const bits = [preview.direction === 'OUTBOUND' ? 'Outgoing call' : 'Incoming call'];
    if (preview.outcome) bits.push(preview.outcome.replace(/_/g, ' ').toLowerCase());
    return bits.join(' · ');
  }
  return preview.text || 'Message';
};

const PreviewIcon = ({ preview }: { preview: LastInteractionPreview | null }) => {
  if (!preview) return null;
  if (preview.type === 'call') {
    return preview.direction === 'OUTBOUND' ? (
      <PhoneOutgoing className="h-3 w-3 shrink-0" />
    ) : (
      <PhoneIncoming className="h-3 w-3 shrink-0" />
    );
  }
  const Icon = preview.channel ? (MESSAGE_CHANNEL_ICONS[preview.channel] ?? MessageSquare) : null;
  return Icon ? <Icon className="h-3 w-3 shrink-0" /> : null;
};

export const ContactListRow = ({
  contact,
  selected,
  onSelect,
}: {
  contact: ContactWithCounts;
  selected: boolean;
  onSelect: (id: string) => void;
}) => {
  const preview = contact.last_interaction_preview ?? null;
  const hasUnread = contact.unread_count > 0;
  const flash = useToolWatchStore((s) => s.flashes[contact.id]);
  const clearFlash = useToolWatchStore((s) => s.clearFlash);
  const acknowledgeToolWatch = useAcknowledgeToolWatch();
  const { byName: tagDefsByName } = useTagDefinitions();
  const assignees = contact.assigned_users ?? [];
  const tags = contact.tags ?? [];
  // An unclaimed contact keeps flashing until someone claims its chat —
  // clicking only stops the flash once the contact is claimed (or when there
  // is no conversation to claim at all).
  const claimed = assignees.length > 0;
  const claimable = (contact._count?.messaging_conversations ?? 0) > 0;
  const flashStopsOnClick = claimed || !claimable;
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number } | null>(null);
  const [callOpen, setCallOpen] = useState(false);
  const [callChannel, setCallChannel] = useState<'pstn' | 'whatsapp' | undefined>(undefined);
  const [callOverride, setCallOverride] = useState(false);
  const [callKey, setCallKey] = useState(0);
  const configurationId = contact.configuration_id ?? undefined;

  return (
    <>
      <button
        type="button"
        draggable
        onDragStart={(e) => {
          // Dropping on a multi-view chat card swaps that chat for this contact.
          e.dataTransfer.setData('application/x-contact-id', contact.id);
          e.dataTransfer.effectAllowed = 'move';
        }}
        onClick={() => {
          if (flash && flashStopsOnClick) {
            clearFlash(contact.id);
            acknowledgeToolWatch.mutate(contact.id);
          }
          onSelect(contact.id);
        }}
        onContextMenu={(e) => {
          e.preventDefault();
          setCtxMenu({ x: e.clientX, y: e.clientY });
        }}
        title={contact.last_cleared ? clearedByLabel(contact.last_cleared) : undefined}
        className={cn(
          'flex w-full cursor-pointer items-center gap-2.5 px-3 py-2.5 text-left transition-colors duration-150 hover:bg-[#f0f2f5] dark:hover:bg-[#202c33]',
          selected && 'bg-[#e9edef] dark:bg-[#2a3942]',
          flash && 'ring-2 ring-amber-400 ring-inset',
        )}
      >
        <div className="relative shrink-0">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#6b7b8d] text-xs font-semibold text-white uppercase">
            {contact.name?.charAt(0) || '?'}
          </div>
          <ActiveIndicator contactId={contact.id} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <span
              className={cn(
                'truncate text-sm text-[#111b21] dark:text-[#e9edef]',
                hasUnread ? 'font-semibold' : 'font-medium',
              )}
            >
              {contact.name}
            </span>
            {flash && (
              <ToolWatchBell
                toolName={flash.toolName}
                size="xs"
                className="ml-auto"
                onDismiss={() => {
                  clearFlash(contact.id);
                  acknowledgeToolWatch.mutate(contact.id);
                }}
              />
            )}
            {contact.last_interaction_at && (
              <span
                className={cn(
                  'shrink-0 text-[11px] tabular-nums',
                  hasUnread ? 'font-medium text-[#00a884]' : 'text-[#667781] dark:text-[#8696a0]',
                )}
              >
                {formatRelative(String(contact.last_interaction_at))}
              </span>
            )}
          </div>
          <div className="mt-0.5 flex items-center justify-between gap-2">
            <span
              className={cn(
                'flex min-w-0 items-center gap-1 text-xs',
                hasUnread
                  ? 'text-[#3b4a54] dark:text-[#d1d7db]'
                  : 'text-[#667781] dark:text-[#8696a0]',
              )}
            >
              <PreviewIcon preview={preview} />
              <span className="truncate">{previewText(preview)}</span>
            </span>
            {hasUnread && (
              <span className="flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full bg-[#00a884] px-1 text-[10px] font-semibold text-white">
                {contact.unread_count}
              </span>
            )}
          </div>
          {(assignees.length > 0 || tags.length > 0) && (
            <div className="mt-1 flex flex-wrap items-center gap-1">
              {assignees.map((user) => (
                <span
                  key={user.id}
                  className="flex items-center gap-0.5 rounded-full bg-[#00a884]/15 px-1.5 py-px text-[10px] font-medium text-[#00a884]"
                  title={`Assigned to ${user.name ?? 'user'}`}
                >
                  <UserCheck className="h-2.5 w-2.5" />
                  {user.name ?? 'User'}
                </span>
              ))}
              {tags.slice(0, 3).map((tag) => (
                <TagBadge key={tag} name={tag} definition={tagDefsByName.get(tag)} size="xs" />
              ))}
              {tags.length > 3 && (
                <span className="text-[10px] text-[#667781] dark:text-[#8696a0]">
                  +{tags.length - 3}
                </span>
              )}
            </div>
          )}
        </div>
      </button>
      {ctxMenu && (
        <ContactContextMenu
          contactId={contact.id}
          position={ctxMenu}
          onClose={() => setCtxMenu(null)}
          initialTags={tags}
          onCall={(ch, override) => {
            setCallChannel(ch);
            setCallOverride(!!override);
            setCallKey((k) => k + 1);
            setCallOpen(true);
          }}
        />
      )}
      {contact.phone && configurationId && (
        <OperatorCallDialog
          key={callKey}
          open={callOpen}
          onOpenChange={setCallOpen}
          contactId={contact.id}
          contactName={contact.name ?? ''}
          contactPhone={contact.phone}
          configurationId={configurationId}
          defaultChannel={callChannel}
          overridePermission={callOverride}
        />
      )}
    </>
  );
};
